import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { Locale, useRouter } from "next/router";

import { HELP_ROUTE } from "@/common/static/routes.static";
import {
  HelpCategoryView,
  getSiblingsForCategory,
} from "@/common/utils/help/help-category.utils";
import {
  enhanceHelpArticleHtml,
  helpLeadRepeatsBody,
} from "@/common/utils/help/help-html.utils";
import { buildHelpDocumentTitle } from "@/common/utils/help/help-seo.utils";
import {
  getHelpArticlePath,
  getHelpCategoryPath,
} from "@/common/utils/help/help-url.utils";
import {
  buildHelpArticleSchema,
  buildHelpFaqPageSchema,
} from "@/common/utils/json-ld.utils";
import { withLocale } from "@/common/utils/url.utils";
import Breadcrumb from "@/components/common/Breadcrumb";
import Canonical from "@/components/common/Canonical";
import HrefLang from "@/components/common/HrefLang";
import { JsonLd } from "@/components/common/JsonLd";
import SEO from "@/components/common/seo";
import { CANONICAL_HOST } from "@/config";
import { HelpArticleBody } from "@/features/help/HelpArticleBody";
import { HelpArticleMeta } from "@/features/help/HelpArticleMeta";
import { HelpArticleNeighbours } from "@/features/help/HelpArticleNeighbours";
import { HelpContactStrip } from "@/features/help/HelpContactStrip";
import { HelpOnThisPage } from "@/features/help/HelpOnThisPage";
import { HelpRelated } from "@/features/help/HelpRelated";
import { HelpSiblingChips } from "@/features/help/HelpSiblingChips";
import { HelpSidebar } from "@/features/help/HelpSidebar";
import { HelpArticleDetail, HelpArticleListItem } from "@/features/help/api";
import { Heading } from "@/ui/cva/Heading";
import { Body } from "@/ui/typography/Body";

const HelpFeedbackWidget = dynamic(
  () =>
    import("@/features/help/HelpFeedbackWidget").then(
      module => module.HelpFeedbackWidget
    ),
  { ssr: false }
);

type HelpArticleViewProps = {
  article: HelpArticleDetail;
  articles: HelpArticleListItem[];
  categories: HelpCategoryView[];
  /** Routed locales; `en-us` only while it routes (WHO-5766). */
  hreflangUrls?: Partial<Record<Locale, string>>;
};

export const HelpArticleView = ({
  article,
  articles,
  categories,
  hreflangUrls,
}: HelpArticleViewProps) => {
  const { t } = useTranslation("common");
  const { locale } = useRouter();
  const helpLabel = t("help-center-title", "Help Center");
  const categorySlug = article.categorySlug ?? "";
  const articlePath = getHelpArticlePath(categorySlug, article.slug);
  const excerpt = article.excerpt || article.summary;
  const { html, headings } = enhanceHelpArticleHtml(
    article.bodyHtml,
    undefined,
    locale
  );
  const showLead = excerpt ? !helpLeadRepeatsBody(excerpt, html) : false;
  const siblings = getSiblingsForCategory(categories, categorySlug);
  const articleUrl = `${CANONICAL_HOST}${withLocale(
    articlePath,
    locale,
    "en"
  )}`;
  const articleSchema = buildHelpArticleSchema({
    title: article.title,
    excerpt,
    summary: article.summary,
    bodyHtml: article.bodyHtml,
    path: articlePath,
    locale: locale ?? "en",
    updatedAt: article.updatedAt,
  });
  const faqSchema = buildHelpFaqPageSchema({
    title: article.title,
    bodyHtml: article.bodyHtml,
    url: articleUrl,
  });
  const seoTitle = buildHelpDocumentTitle(
    article.title,
    t("help-center-title", "Help Center")
  );

  return (
    <div className="mx-auto mb-20 flex w-full max-w-[1100px] flex-col gap-y-8 px-4 md:px-6 lg:px-12">
      <SEO
        title={seoTitle}
        description={
          excerpt ||
          t(
            "help-article-seo-description",
            "Help article from the Whoppah Help Center."
          )
        }
        ogType="article"
      >
        {article.updatedAt ? (
          <meta property="article:modified_time" content={article.updatedAt} />
        ) : null}
      </SEO>
      {hreflangUrls ? <HrefLang urlMap={hreflangUrls} /> : null}
      <Canonical />
      <JsonLd item={articleSchema} />
      <JsonLd item={faqSchema} />
      <div className="flex flex-col gap-x-10 gap-y-8 lg:flex-row">
        <HelpSidebar
          articles={articles}
          categorySlug={categorySlug}
          currentSlug={article.slug}
        />
        <article className="min-w-0 max-w-[720px] flex-1">
          <Breadcrumb
            items={[
              { label: helpLabel, href: HELP_ROUTE },
              ...(article.categoryTitle && categorySlug
                ? [
                    {
                      label: article.categoryTitle,
                      href: getHelpCategoryPath(categorySlug),
                    },
                  ]
                : []),
              { label: article.title },
            ]}
          />
          <HelpSiblingChips categories={siblings} />
          <Heading as="h1" size="h1" className="mt-4">
            {article.title}
          </Heading>
          <HelpArticleMeta
            categoryTitle={article.categoryTitle}
            categorySlug={article.categorySlug}
            updatedAt={article.updatedAt}
            readingTimeMinutes={article.readingTimeMinutes}
            locale={locale ?? "en"}
          />
          {showLead ? (
            <Body as="p" className="mt-4 text-lg text-grey-800 md:text-lg">
              {excerpt}
            </Body>
          ) : null}
          <HelpOnThisPage headings={headings} />
          <HelpArticleBody html={html} className="mt-6" />
          <HelpFeedbackWidget articleId={article.id} />
          <HelpArticleNeighbours
            previous={article.previous}
            next={article.next}
          />
          <HelpRelated articles={article.related} />
          <HelpContactStrip />
        </article>
      </div>
    </div>
  );
};
